import React, { useMemo, useState } from 'react';
import {
    Activity,
    AlertTriangle,
    CalendarDays,
    ChevronDown,
    ChevronRight,
    Loader2,
    Map as MapIcon,
    RefreshCw,
    Users as UsersIcon,
} from 'lucide-react';
import {
    ZEITRAUM_VORGABEN,
    UserActivityRow,
    useUserActivity,
} from '../../hooks/useUserActivity';
import {
    formatClock,
    formatDay,
    formatDayTime,
    formatDuration,
    localDayKey,
    parseDayKey,
} from '../../services/userActivityStats';
import { ProjectSession, RouteDay, UserActivityCloseReason } from '../../services/userActivityTypes';
import { DailyActivityChart, HourHistogramChart, TopProjectsChart, WeekdayHourHeatmap } from './UserActivityCharts';
import { UserActivityRouteMap } from './UserActivityRouteMap';
import { useGesperrteKonten } from '../../hooks/useGesperrteKonten';
import { gesperrtZusatz } from '../../services/gesperrteKonten';

// Admin-Tab „Nutzeraktivitaet".
//
// Duenner Container (Standing Decision): Laden und Zustand stecken in
// hooks/useUserActivity.ts, gerechnet wird pur in
// services/userActivityStats.ts, die Diagramme sind praesentational.
//
// Die Statistik beginnt mit der Migration Nr. 46 bei null — es gibt keine
// Rueckwirkung auf die Zeit davor, und es wird auch keine geschaetzt.

// ─── Formatierung ───────────────────────────────────────────────────────────

// Datum dd.MM.yyyy, Uhrzeit HH:mm und Dauer kommen aus userActivityStats —
// EINE Regel je Format, damit Tabelle, Zeitachse und Karte nicht auseinander-
// laufen.

const WOCHENTAG_LANG = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
const wochentag = (day: string): string => {
    const d = parseDayKey(day);
    return d ? WOCHENTAG_LANG[d.getDay()] : '';
};

const GRUND_TEXT: Record<UserActivityCloseReason, string> = {
    manual: 'manuell verlassen',
    idle: 'Leerlauf',
    logout: 'Abmeldung',
    unload: 'Browser geschlossen',
    switch: 'Projektwechsel',
    hidden: 'in den Hintergrund',
    recovered: 'nachgetragen',
    maintenance: 'Bauphase',
};

/**
 * Der Erklärtext am Abzeichen. Nur dort, wo die Bezeichnung allein in die
 * Irre führen könnte: 'hidden' ist mobil der Normalfall und kein Fehler, und
 * bei 'recovered' MUSS dranstehen, dass das Ende bis zu einem Herzschlag zu
 * früh liegt — sonst liest jemand eine Dauer als exakt, die es nicht ist.
 */
const GRUND_HINWEIS: Partial<Record<UserActivityCloseReason, string>> = {
    hidden: 'App-Wechsel oder Bildschirm aus — Zeitpunkt exakt, die Pause zählt nicht mit.',
    recovered: 'Die Seite starb ohne Abmeldung; das Ende stammt aus dem letzten Herzschlag und liegt bis zu 30 s zu früh.',
};

const GRUND_KLASSE: Record<UserActivityCloseReason, string> = {
    manual: 'bg-slate-100 text-slate-600 border-slate-200',
    idle: 'bg-amber-50 text-amber-700 border-amber-200',
    logout: 'bg-violet-50 text-violet-700 border-violet-200',
    unload: 'bg-slate-100 text-slate-600 border-slate-200',
    switch: 'bg-blue-50 text-blue-700 border-blue-200',
    hidden: 'bg-slate-100 text-slate-600 border-slate-200',
    recovered: 'bg-orange-50 text-orange-700 border-orange-200',
    maintenance: 'bg-rose-50 text-rose-700 border-rose-200',
};

// ─── Uebersichtstabelle ─────────────────────────────────────────────────────

type SortKey =
    | 'name'
    | 'firstStartAt'
    | 'lastStartAt'
    | 'startCount'
    | 'projectOpenCount'
    | 'resumeCount'
    | 'avgDurationMs'
    | 'totalTrackedMs'
    | 'openSessionCount'
    | 'activeDays';

const SPALTEN: { key: SortKey; label: string; rechts?: boolean }[] = [
    { key: 'name', label: 'Nutzer' },
    { key: 'firstStartAt', label: 'Erster Start' },
    { key: 'lastStartAt', label: 'Letzter Start' },
    { key: 'startCount', label: 'Starts', rechts: true },
    { key: 'projectOpenCount', label: 'Projekte geöffnet', rechts: true },
    // Eigene Spalte statt eines Aufschlags auf „Projekte geöffnet": eine
    // Wiederaufnahme ist keine Öffnung, aber sie ist auch nicht nichts.
    { key: 'resumeCount', label: 'Wiederaufnahmen', rechts: true },
    { key: 'avgDurationMs', label: 'Ø Verweildauer', rechts: true },
    { key: 'totalTrackedMs', label: 'Erfasste Zeit', rechts: true },
    { key: 'openSessionCount', label: 'Offene Sitzungen', rechts: true },
    { key: 'activeDays', label: 'Aktive Tage', rechts: true },
];

const sortWert = (row: UserActivityRow, key: SortKey): string | number => {
    switch (key) {
        case 'name':
            return row.name.toLowerCase();
        case 'firstStartAt':
            return row.summary.firstStartAt ? new Date(row.summary.firstStartAt).getTime() : -1;
        case 'lastStartAt':
            return row.summary.lastStartAt ? new Date(row.summary.lastStartAt).getTime() : -1;
        case 'avgDurationMs':
            // „keine abgeschlossene Sitzung" ist nicht null Minuten, sondern
            // gar kein Wert — beim Sortieren landet er unten, nicht bei 0.
            return row.avgDurationMs ?? -1;
        case 'startCount':
            return row.summary.startCount;
        case 'projectOpenCount':
            return row.summary.projectOpenCount;
        case 'resumeCount':
            return row.summary.resumeCount;
        case 'totalTrackedMs':
            return row.summary.totalTrackedMs;
        case 'openSessionCount':
            return row.summary.openSessionCount;
        case 'activeDays':
            return row.summary.activeDays;
    }
};

// ─── Zeitachse ──────────────────────────────────────────────────────────────

interface TagesBlock {
    day: string;
    sessions: ProjectSession[];
}

const nachTagen = (sessions: ProjectSession[]): TagesBlock[] => {
    const nach = new Map<string, ProjectSession[]>();
    sessions.forEach((s) => {
        const tag = localDayKey(s.openedAt);
        if (!tag) return;
        const liste = nach.get(tag);
        if (liste) liste.push(s);
        else nach.set(tag, [s]);
    });
    return Array.from(nach.entries())
        .map(([day, liste]) => ({
            day,
            sessions: liste.slice().sort((a, b) => a.openedAt.localeCompare(b.openedAt)),
        }))
        .sort((a, b) => b.day.localeCompare(a.day));
};

/**
 * Wie viele Sitzungen eines Blocks sind Wiederaufnahmen?
 *
 * Wiederaufnahme heisst: die Anwendung hat wiederhergestellt, was der
 * Anwender schon offen hatte — Rueckkehr aus dem Hintergrund (App-Wechsel am
 * Handy) ODER Neuladen der Seite (F5, Version-Update, wiederhergestellter
 * Tab).
 *
 * Sie sind echte Sitzungen mit echter Zeit, zaehlen aber NICHT als
 * Projektoeffnung (sonst zaehlte jeder App-Wechsel und jedes Neuladen als
 * „Projekt geoeffnet"). Wo die Zeitachse mehr Zeilen zeigt, als „Projekte
 * geoeffnet" ausweist, muss die Differenz danebenstehen.
 */
const wiederaufnahmen = (sessions: ProjectSession[]): number =>
    sessions.reduce((n, s) => n + (s.resumed ? 1 : 0), 0);

/**
 * Feste leere Listen fuer Tage ohne Route. Der Zeicheneffekt der Karte haengt
 * an `routeDay.points`; ein frisch erzeugtes `[]` waere jedes Mal ein neuer
 * Wert und wuerde den Kartenausschnitt zuruecksetzen.
 */
const LEERE_PUNKTE: RouteDay['points'] = [];
const LEERE_FEHLSTELLEN: RouteDay['missingCoordinates'] = [];

// ─── Komponente ─────────────────────────────────────────────────────────────

export const AdminUserActivityView: React.FC = () => {
    // Filter über Vergangenes: Gesperrte (Nr. 148) bleiben wählbar, markiert.
    const gesperrteKonten = useGesperrteKonten();
    const {
        loading,
        error,
        tableMissing,
        reload,
        filter,
        setFilter,
        range,
        users,
        profilesWithoutCompany,
        ownCompanyMissing,
        isEmpty,
        truncated,
        truncatedUserCount,
        eventLimit,
        boundaryIncomplete,
        rows,
        charts,
        topProjects,
        routeDaysByUser,
        requestRouteCoordinates,
        routeCoordinatesLoading,
        routeCoordinatesLoaded,
        routeCoordinatesFailed,
        routeCoordinatesPartial,
        routeCoordinatesPartialCount,
    } = useUserActivity();

    const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'lastStartAt', dir: 'desc' });
    const [offen, setOffen] = useState<Record<string, boolean>>({});
    const [karte, setKarte] = useState<{ userId: string; day: string } | null>(null);

    const sortierteZeilen = useMemo(() => {
        const liste = rows.slice();
        liste.sort((a, b) => {
            const va = sortWert(a, sort.key);
            const vb = sortWert(b, sort.key);
            let d = 0;
            if (typeof va === 'string' || typeof vb === 'string') d = String(va).localeCompare(String(vb));
            else d = (va as number) - (vb as number);
            return sort.dir === 'asc' ? d : -d;
        });
        return liste;
    }, [rows, sort]);

    // Genau EIN Kartenausschnitt ist offen; sein RouteDay wird hier einmal
    // bestimmt. Vorher entstand fuer Tage ohne Route bei JEDEM Render ein
    // frisches Leerobjekt — der Zeicheneffekt der Karte lief daraufhin erneut
    // und setzte Ausschnitt und Zoom des Betrachters zurueck.
    const offeneRoute = useMemo<RouteDay | null>(() => {
        if (!karte) return null;
        return (
            (routeDaysByUser[karte.userId] || []).find((rd) => rd.day === karte.day) || {
                day: karte.day,
                points: LEERE_PUNKTE,
                missingCoordinates: LEERE_FEHLSTELLEN,
            }
        );
    }, [karte, routeDaysByUser]);

    // Verworfene Schliessen-Ereignisse ueber die angezeigten Nutzer.
    //
    // Seit dem Nachladen der Randereignisse ist das kein Zeitraumeffekt mehr:
    // ein Schliessen, dessen Oeffnung nur ausserhalb des Zeitraums lag, wird
    // gepaart und taucht hier NICHT auf. Was uebrig bleibt, ist ein Befund an
    // den Daten (verlorenes Beacon, verstellte Uhr) — oder ein abgeschnittener
    // alter Rand, und den nennt das Banner darueber beim Namen.
    const verworfeneCloses = useMemo(
        () => rows.reduce((n, r) => n + (r.summary.discardedCloseCount || 0), 0),
        [rows],
    );

    // Sitzungen, die VOR dem Zeitraum begonnen haben und in ihn hineinreichen.
    // Ihre Zeit steht im Zeitraum davor, nicht hier — gekappt wird sie nicht.
    // Ohne diese Zeile faenden Leser den Unterschied zwischen zwei
    // aneinandergrenzenden Zeitraeumen nicht wieder.
    const randSitzungen = useMemo(
        () => rows.reduce((n, r) => n + (r.summary.precedingSessionCount || 0), 0),
        [rows],
    );

    // Ende des geltenden Zeitraums, um Sitzungen zu kennzeichnen, die darueber
    // hinauslaufen. Ihre Dauer wird voll ausgewiesen und NICHT an der Grenze
    // abgeschnitten — dann muss aber danebenstehen, dass sie darueber hinausgeht.
    const bisZeit = useMemo(() => Date.parse(range.bisIso), [range.bisIso]);

    const umschalten = (key: SortKey) =>
        setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }));

    const istAufgeklappt = (userId: string): boolean =>
        offen[userId] ?? (sortierteZeilen.length === 1 || filter.userId !== 'all');

    // ── Filterleiste ────────────────────────────────────────────────────────

    // Welcher Zeitraum GILT, steht immer da: die freie Eingabe kommt auch mit
    // einer einzigen Grenze aus, und dann ist kein Vorgabeknopf aktiv.
    const zeitraumText = range.vonIso
        ? `${formatDay(localDayKey(range.vonIso))} – ${formatDay(localDayKey(range.bisIso))}`
        : `seit dem ersten erfassten Ereignis – ${formatDay(localDayKey(range.bisIso))}`;

    const filterleiste = (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex flex-wrap items-end gap-4">
            <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                    Zeitraum
                </label>
                <div className="flex gap-1.5">
                    {ZEITRAUM_VORGABEN.map((t) => {
                        const aktiv = !filter.von && !filter.bis && filter.tage === t;
                        return (
                            <button
                                key={t}
                                onClick={() => setFilter({ ...filter, tage: t, von: '', bis: '' })}
                                className={`px-3 py-2 text-sm font-bold rounded-xl border transition-all ${
                                    aktiv
                                        ? 'bg-white border-indigo-200 text-indigo-600 shadow-sm'
                                        : 'bg-slate-50 border-transparent text-slate-500 hover:bg-white hover:border-slate-200'
                                }`}
                            >
                                {t} Tage
                            </button>
                        );
                    })}
                </div>
            </div>

            <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                    Frei von / bis
                </label>
                <div className="flex items-center gap-1.5">
                    <input
                        type="date"
                        value={filter.von}
                        onChange={(e) => setFilter({ ...filter, von: e.target.value })}
                        className="px-3 py-2 text-sm rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-300 outline-none"
                    />
                    <span className="text-slate-400 text-sm">–</span>
                    <input
                        type="date"
                        value={filter.bis}
                        onChange={(e) => setFilter({ ...filter, bis: e.target.value })}
                        className="px-3 py-2 text-sm rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-300 outline-none"
                    />
                    {(filter.von || filter.bis) && (
                        <button
                            onClick={() => setFilter({ ...filter, von: '', bis: '' })}
                            className="px-2.5 py-2 text-xs font-bold rounded-xl border border-transparent bg-slate-50 text-slate-500 hover:bg-white hover:border-slate-200"
                        >
                            zurücksetzen
                        </button>
                    )}
                </div>
            </div>

            <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                    Nutzer
                </label>
                <select
                    value={filter.userId}
                    onChange={(e) => setFilter({ ...filter, userId: e.target.value })}
                    className="px-3 py-2 text-sm rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-300 outline-none min-w-[200px]"
                >
                    <option value="all">Alle Nutzer</option>
                    {users.map((u) => (
                        <option key={u.id} value={u.id}>
                            {[u.first_name, u.last_name].filter(Boolean).join(' ') || (u as any).email || u.id}{gesperrtZusatz(u.id, gesperrteKonten)}
                        </option>
                    ))}
                </select>
            </div>

            <button
                onClick={reload}
                className="px-4 py-2 text-sm font-bold rounded-xl border border-slate-200 bg-slate-50 text-slate-600 hover:bg-white flex items-center gap-2 transition-all"
            >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Aktualisieren
            </button>

            <div className="w-full flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3 text-[11px] text-slate-500">
                <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
                <span className="font-bold uppercase tracking-wider text-slate-400">Geltender Zeitraum</span>
                <span className="font-bold tabular-nums text-slate-600">{zeitraumText}</span>
                {range.ungueltig && (
                    <span className="px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200 font-bold">
                        Freie Eingabe verdreht (von liegt nach bis) — es gilt die Tagesvorgabe
                    </span>
                )}
                {/* Was der Firmenfilter verworfen hat, steht hier. Ohne diese
                    Zeile liest sich ein fehlender Firmen-Backfill wie „keine
                    Daten": die Ereignisse dieser Profile werden gar nicht
                    geladen. */}
                {ownCompanyMissing && (
                    <span className="px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200 font-bold">
                        Am eigenen Profil fehlt die Firmenangabe — sichtbar ist deshalb nur die eigene Aktivität
                    </span>
                )}
                {profilesWithoutCompany > 0 && (
                    <span className="px-2 py-0.5 rounded-full border bg-slate-100 text-slate-600 border-slate-200 font-bold">
                        {profilesWithoutCompany} Profil(e) ohne Firmenangabe ausgeblendet — ihre Ereignisse werden nicht
                        geladen (company_id/company_name am Profil nachtragen)
                    </span>
                )}
            </div>
        </div>
    );

    // ── Zustaende ───────────────────────────────────────────────────────────

    if (loading) {
        return (
            <div className="space-y-6">
                {filterleiste}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm py-16 flex flex-col items-center gap-3">
                    <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
                    <span className="text-slate-400 text-xs font-bold">Aktivitätsdaten werden geladen …</span>
                </div>
            </div>
        );
    }

    if (tableMissing) {
        return (
            <div className="space-y-6">
                {filterleiste}
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                        <p className="text-sm font-bold text-amber-800">Erfassung noch nicht eingerichtet</p>
                        <p className="text-sm text-amber-700 mt-1">
                            Die Tabelle <code className="font-mono">user_activity_events</code> fehlt in dieser Umgebung.
                            Die Migration Nr. 46 muss einmalig im Supabase-Editor laufen, danach beginnt die Erfassung.
                            Rückwirkende Daten gibt es nicht.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-6">
                {filterleiste}
                <div className="bg-red-50 border border-red-200 rounded-2xl p-6 flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <p className="text-sm font-bold text-red-800">Aktivitätsdaten konnten nicht geladen werden</p>
                        <p className="text-sm text-red-700 mt-1 break-words">{error}</p>
                        <button
                            onClick={reload}
                            className="mt-3 px-3 py-1.5 text-xs font-bold rounded-lg bg-white border border-red-200 text-red-700 hover:bg-red-50"
                        >
                            Erneut versuchen
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (isEmpty || sortierteZeilen.length === 0) {
        return (
            <div className="space-y-6">
                {filterleiste}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm py-16 flex flex-col items-center gap-2">
                    <Activity className="w-7 h-7 text-slate-200" />
                    <span className="text-slate-400 text-sm font-bold">Keine Daten im Zeitraum</span>
                    <span className="text-slate-400 text-xs">
                        Erfasst wird ab dem Zeitpunkt der Einrichtung — vor diesem Datum existieren keine Aufzeichnungen.
                    </span>
                </div>
            </div>
        );
    }

    // ── Inhalt ──────────────────────────────────────────────────────────────

    return (
        <div className="space-y-6">
            {filterleiste}

            {truncated && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                        <p className="text-sm font-bold text-amber-800">Zeitraum nur teilweise geladen</p>
                        <p className="text-sm text-amber-700 mt-1">
                            Die Obergrenze von {eventLimit.toLocaleString('de-DE')} Ereignissen hat gegriffen. Gelesen
                            wird von den jüngsten Ereignissen rückwärts — die ältesten Tage des gewählten Zeitraums
                            fehlen deshalb in dieser Auswertung
                            {truncatedUserCount > 0 ? `, und zwar bei bis zu ${truncatedUserCount} Nutzer(n)` : ''}. Das
                            Kontingent wird je Nutzergruppe geführt: es bleibt <strong>kein Nutzer ungelesen</strong>,
                            gekürzt wird nur am alten Rand. Zeitraum verkleinern oder einen einzelnen Nutzer wählen,
                            dann ist das Bild wieder vollständig.
                        </p>
                        {verworfeneCloses > 0 && (
                            <p className="text-sm text-amber-700 mt-1">
                                Dabei wurden <strong>{verworfeneCloses.toLocaleString('de-DE')}</strong> Schließen-
                                Ereignis(se) verworfen, deren Öffnung außerhalb der geladenen Reihe liegt. Ihre Dauer
                                fehlt in „Erfasste Zeit" — geschätzt wird sie nicht.
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* Uebersichtstabelle */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
                    <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                        <UsersIcon className="w-4 h-4 text-indigo-500" /> Übersicht je Nutzer
                    </h3>
                    <span className="text-[11px] text-slate-400">
                        {sortierteZeilen.length} Nutzer mit Aktivität · Spaltenkopf klicken zum Sortieren
                    </span>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse min-w-[980px]">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase font-bold text-slate-500 tracking-wider">
                                {SPALTEN.map((s) => (
                                    <th
                                        key={s.key}
                                        onClick={() => umschalten(s.key)}
                                        className={`px-4 py-3 cursor-pointer select-none hover:text-indigo-600 transition-colors ${
                                            s.rechts ? 'text-right' : ''
                                        } ${sort.key === s.key ? 'text-indigo-600' : ''}`}
                                    >
                                        {s.label}
                                        {sort.key === s.key && <span className="ml-1">{sort.dir === 'asc' ? '▲' : '▼'}</span>}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {sortierteZeilen.map((r) => (
                                <tr key={r.userId} className="hover:bg-slate-50/60 transition-colors">
                                    <td className="px-4 py-3 text-sm font-bold text-slate-700">{r.name}</td>
                                    <td className="px-4 py-3 text-sm text-slate-500 tabular-nums whitespace-nowrap">
                                        {formatDayTime(r.summary.firstStartAt)}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-slate-500 tabular-nums whitespace-nowrap">
                                        {formatDayTime(r.summary.lastStartAt)}
                                    </td>
                                    <td className="px-4 py-3 text-right text-sm text-slate-600 tabular-nums">
                                        {r.summary.startCount}
                                    </td>
                                    <td
                                        className="px-4 py-3 text-right text-sm text-slate-600 tabular-nums"
                                        title="Ohne Wiederaufnahmen (Rückkehr aus dem Hintergrund, Neuladen der Seite) — die stehen in der Spalte daneben."
                                    >
                                        {r.summary.projectOpenCount}
                                    </td>
                                    <td className="px-4 py-3 text-right text-sm tabular-nums">
                                        {r.summary.resumeCount > 0 ? (
                                            <span
                                                className="px-2 py-0.5 rounded-full text-[11px] font-bold border bg-violet-50 text-violet-700 border-violet-200"
                                                title="Fortsetzung eines bereits offenen Projekts: Rückkehr aus dem Hintergrund (App-Wechsel am Handy) oder Neuladen der Seite (F5, Version-Update). Eigene Sitzung mit eigener Dauer, aber keine neue Projektöffnung."
                                            >
                                                {r.summary.resumeCount}×
                                            </span>
                                        ) : (
                                            <span className="text-slate-300">—</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-right text-sm text-slate-600 tabular-nums whitespace-nowrap">
                                        {r.avgDurationMs === null ? (
                                            <span className="text-slate-300">—</span>
                                        ) : (
                                            formatDuration(r.avgDurationMs)
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-right text-sm font-bold text-slate-700 tabular-nums whitespace-nowrap">
                                        {formatDuration(r.summary.totalTrackedMs)}
                                    </td>
                                    <td className="px-4 py-3 text-right text-sm tabular-nums">
                                        {r.summary.openSessionCount > 0 ? (
                                            <span className="px-2 py-0.5 rounded-full text-[11px] font-bold border bg-amber-50 text-amber-700 border-amber-200">
                                                {r.summary.openSessionCount} offen
                                            </span>
                                        ) : (
                                            <span className="text-slate-300">—</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-right text-sm text-slate-600 tabular-nums">
                                        {r.summary.activeDays}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="px-5 py-2.5 border-t border-slate-100 bg-slate-50/60 text-[11px] text-slate-400 space-y-1">
                    <p>
                        „Erfasste Zeit" und „Ø Verweildauer" zählen ausschließlich abgeschlossene Sitzungen. Offene
                        Sitzungen werden getrennt ausgewiesen und niemals als 0 Minuten verrechnet.
                    </p>
                    <p>
                        „Projekte geöffnet" zählt <strong>ohne Wiederaufnahmen</strong>: kehrt die App aus dem
                        Hintergrund zurück (Anruf, App-Wechsel am Handy) oder wird die Seite neu geladen (F5,
                        Version-Update, wiederhergestellter Tab), beginnt zwar eine neue Sitzung mit eigener Dauer,
                        aber niemand hat ein Projekt geöffnet — der Anwender saß längst darin. Ihre Zeit steckt in
                        „Erfasste Zeit", ihre Zahl in der Spalte „Wiederaufnahmen".
                    </p>
                    {/* Die Regel an der Zeitraumgrenze. Sie steht hier, weil sie
                        erklaert, warum zwei aneinandergrenzende Zeitraeume sich
                        nicht ueberlappen und trotzdem nichts fehlt. */}
                    <p>
                        Eine Sitzung zählt <strong>ganz zu dem Zeitraum, in dem sie geöffnet wurde</strong> — mit ihrer
                        vollen gemessenen Dauer, auch wenn sie über das Ende des Zeitraums hinausläuft. An der Grenze
                        wird nichts abgeschnitten und nichts hochgerechnet. Umgekehrt zählt eine Sitzung, die vor dem
                        Zeitraum begann, im Zeitraum davor und nicht hier.
                    </p>
                    {randSitzungen > 0 && (
                        <p>
                            {randSitzungen.toLocaleString('de-DE')} Sitzung(en) begannen vor diesem Zeitraum und reichen
                            in ihn hinein. Ihre Zeit steht im vorherigen Zeitraum, nicht in „Erfasste Zeit" — ihr
                            Schließen ist deshalb <strong>kein Datenfehler</strong> und wird auch nicht als solcher
                            gezählt.
                        </p>
                    )}
                    {/* Ohne Abschneiden ist ein verworfenes Schliessen ein
                        Datenbefund (Beacon verloren, Uhr verstellt) — auch der
                        wird beziffert statt verschwiegen. */}
                    {!truncated && verworfeneCloses > 0 && (
                        <p>
                            {verworfeneCloses.toLocaleString('de-DE')} Schließen-Ereignis(se) ohne zugehörige Öffnung
                            wurden verworfen — eine Sitzung mit geratenem Anfang wird nicht erfunden. Die
                            Zeitraumgrenze scheidet als Ursache aus: die Öffnungen davor werden eigens nachgeladen.
                        </p>
                    )}
                    {boundaryIncomplete && (
                        <p className="text-amber-700">
                            Die Ereignisse an den Rändern des Zeitraums konnten nicht vollständig nachgeladen werden.
                            Einzelne Sitzungen können deshalb als „offen" erscheinen, obwohl sie nach dem Zeitraum
                            geschlossen wurden, und einzelne Schließen als verworfen gelten, obwohl nur ihre Öffnung
                            davor liegt. Kleinerer Zeitraum oder einzelner Nutzer schafft Abhilfe.
                        </p>
                    )}
                </div>
            </div>

            {/* Diagramme */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <DailyActivityChart days={charts.perDay} />
                <HourHistogramChart hours={charts.hourHistogram} />
                <WeekdayHourHeatmap matrix={charts.weekdayHourHeatmap} />
                <TopProjectsChart rows={topProjects} />
            </div>

            {/* Zeitachse je Nutzer/Tag */}
            <div className="space-y-4">
                {sortierteZeilen.map((r) => {
                    const bloecke = nachTagen(r.summary.projectSessions);
                    const auf = istAufgeklappt(r.userId);
                    return (
                        <div key={r.userId} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                            <button
                                onClick={() => setOffen((o) => ({ ...o, [r.userId]: !auf }))}
                                className="w-full px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3 hover:bg-slate-50/60 transition-colors text-left"
                            >
                                <span className="text-sm font-bold text-slate-800 flex items-center gap-2">
                                    {auf ? (
                                        <ChevronDown className="w-4 h-4 text-slate-400" />
                                    ) : (
                                        <ChevronRight className="w-4 h-4 text-slate-400" />
                                    )}
                                    <CalendarDays className="w-4 h-4 text-blue-500" /> Zeitachse — {r.name}
                                </span>
                                <span className="text-[11px] text-slate-400">
                                    {bloecke.length} Tag(e) · {r.summary.projectSessions.length} Projektsitzung(en)
                                    {r.summary.resumeCount > 0 &&
                                        ` · davon ${r.summary.resumeCount} Wiederaufnahme(n)`}
                                </span>
                            </button>

                            {auf && (
                                <div className="p-5 space-y-5">
                                    {bloecke.length === 0 && (
                                        <p className="py-8 text-center text-slate-300 text-xs font-medium">
                                            Keine Projektsitzungen im Zeitraum
                                        </p>
                                    )}

                                    {bloecke.map((b) => {
                                        const route = (routeDaysByUser[r.userId] || []).find((rd) => rd.day === b.day);
                                        const karteOffen = karte?.userId === r.userId && karte?.day === b.day;
                                        const verortet = route ? route.points.length : 0;
                                        const tagesWiederaufnahmen = wiederaufnahmen(b.sessions);

                                        return (
                                            <div key={b.day}>
                                                <div className="flex items-center justify-between gap-3 mb-2">
                                                    <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider">
                                                        {formatDay(b.day)}
                                                        <span className="ml-2 font-medium text-slate-400 normal-case tracking-normal">
                                                            {wochentag(b.day)} · {b.sessions.length} Sitzung(en)
                                                            {tagesWiederaufnahmen > 0 &&
                                                                `, davon ${tagesWiederaufnahmen} Wiederaufnahme(n)`}
                                                        </span>
                                                    </h4>
                                                    <button
                                                        onClick={() => {
                                                            // Erst hier duerfen die Projektkoordinaten geladen
                                                            // werden — ohne offene Karte braucht sie niemand.
                                                            if (!karteOffen) requestRouteCoordinates();
                                                            setKarte(karteOffen ? null : { userId: r.userId, day: b.day });
                                                        }}
                                                        className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition-all flex items-center gap-1.5 ${
                                                            karteOffen
                                                                ? 'bg-white border-blue-200 text-blue-600 shadow-sm'
                                                                : 'bg-slate-50 border-transparent text-slate-500 hover:bg-white hover:border-slate-200'
                                                        }`}
                                                    >
                                                        <MapIcon className="w-3.5 h-3.5" />
                                                        {karteOffen ? 'Karte ausblenden' : 'Karte anzeigen'}
                                                        {/* Die Zahl erst nennen, wenn die Koordinaten da sind —
                                                            vorher waere sie immer „0 verortet". */}
                                                        {routeCoordinatesLoaded && (
                                                            <span className="text-slate-400 font-medium">
                                                                ({verortet} verortet
                                                                {routeCoordinatesPartial ? ', unvollständig' : ''})
                                                            </span>
                                                        )}
                                                    </button>
                                                </div>

                                                <ul className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden">
                                                    {b.sessions.map((s, i) => (
                                                        <li
                                                            key={`${s.sessionId}-${s.projectId}-${s.openedAt}-${i}`}
                                                            className="px-4 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 hover:bg-slate-50/60 transition-colors"
                                                        >
                                                            <span className="w-6 shrink-0 text-[11px] font-black text-slate-300 tabular-nums">
                                                                {i + 1}
                                                            </span>
                                                            <span className="text-sm font-bold text-slate-700 truncate max-w-[280px]">
                                                                {s.projectName || 'Unbenanntes Projekt'}
                                                            </span>
                                                            {/* Eine Wiederaufnahme sieht sonst aus wie ein
                                                                zweites Öffnen desselben Projekts — und
                                                                widerspräche der Zahl in „Projekte geöffnet". */}
                                                            {s.resumed && (
                                                                <span
                                                                    className="px-2 py-0.5 rounded-full text-[11px] font-bold border bg-violet-50 text-violet-700 border-violet-200"
                                                                    title="Fortsetzung eines bereits offenen Projekts — Rückkehr aus dem Hintergrund (App-Wechsel am Handy) oder Neuladen der Seite (F5, Version-Update). Keine neue Projektöffnung und deshalb nicht in der Spalte „Projekte geöffnet“."
                                                                >
                                                                    Wiederaufnahme
                                                                </span>
                                                            )}
                                                            <span className="font-mono text-[11px] bg-slate-100 px-1.5 py-0.5 rounded text-slate-500">
                                                                #{s.projectNumber || '—'}
                                                            </span>
                                                            <span className="text-sm text-slate-500 tabular-nums whitespace-nowrap">
                                                                {formatClock(s.openedAt)} –{' '}
                                                                {s.closedAt ? formatClock(s.closedAt) : '…'}
                                                            </span>
                                                            <span className="ml-auto flex items-center gap-2">
                                                                {s.durationMs === null ? (
                                                                    <span className="px-2 py-0.5 rounded-full text-[11px] font-bold border bg-amber-50 text-amber-700 border-amber-200">
                                                                        offen
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-sm font-bold text-slate-700 tabular-nums whitespace-nowrap">
                                                                        {formatDuration(s.durationMs)}
                                                                    </span>
                                                                )}
                                                                {/* Voll ausgewiesen, aber gekennzeichnet: die
                                                                    Dauer laeuft ueber das Zeitraumende hinaus.
                                                                    Kappen waere die bequemere und die
                                                                    unehrlichere Anzeige. */}
                                                                {s.closedAt &&
                                                                    Number.isFinite(bisZeit) &&
                                                                    Date.parse(s.closedAt) > bisZeit && (
                                                                        <span
                                                                            className="px-2 py-0.5 rounded-full text-[11px] font-bold border bg-sky-50 text-sky-700 border-sky-200"
                                                                            title="Erst nach dem Ende des gewählten Zeitraums geschlossen. Ausgewiesen ist die volle gemessene Dauer — an der Zeitraumgrenze wird nichts abgeschnitten."
                                                                        >
                                                                            endet nach dem Zeitraum
                                                                        </span>
                                                                    )}
                                                                {s.closeReason && (
                                                                    <span
                                                                        className={`px-2 py-0.5 rounded-full text-[11px] font-bold border ${GRUND_KLASSE[s.closeReason]}`}
                                                                        title={GRUND_HINWEIS[s.closeReason]}
                                                                    >
                                                                        {GRUND_TEXT[s.closeReason]}
                                                                    </span>
                                                                )}
                                                            </span>
                                                        </li>
                                                    ))}
                                                </ul>

                                                {karteOffen && offeneRoute && (
                                                    <div className="mt-3">
                                                        <UserActivityRouteMap
                                                            routeDay={offeneRoute}
                                                            userLabel={r.name}
                                                            dayLabel={formatDay(b.day)}
                                                            coordinatesLoading={routeCoordinatesLoading}
                                                            coordinatesFailed={routeCoordinatesFailed}
                                                            coordinatesPartial={routeCoordinatesPartial}
                                                            coordinatesPartialCount={routeCoordinatesPartialCount}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
